import React, { useEffect, useMemo, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useParams } from 'react-router-dom';
import { PROJECTS } from '../data/projects';
import '../styles/projects.scss';

gsap.registerPlugin(ScrollTrigger);

const Project = () => {
  const heroSectionRef = useRef(null);
  const heroMediaRef = useRef(null);
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);

  if (!project) {
    return (
      <div className="project-page">
        <div className="wrapper grid-display">
          <div className="row">
            <div className="col-12">
              <p className="mono">Project not found</p>
              {/* <Link className="mono" to="/work">
                Back to work
              </Link> */}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const metaRows = [...(project.meta || [])];

  const heroImage = useMemo(() => {
    if (project.heroBackgroundImage) {
      return project.heroBackgroundImage;
    }

    const firstImageBlock = project.blocks?.find((block) => block.type === 'image' && block.src);
    if (firstImageBlock) {
      return firstImageBlock.src;
    }

    const firstTwoImageBlock = project.blocks?.find(
      (block) => block.type === 'two-images' && block.images?.length
    );

    if (firstTwoImageBlock) {
      return firstTwoImageBlock.images[0].src;
    }

    return null;
  }, [project]);

  useEffect(() => {
    if (!heroImage) {
      return undefined;
    }

    const heroEl = heroSectionRef.current;
    const mediaEl = heroMediaRef.current;
    if (!heroEl || !mediaEl) {
      return undefined;
    }

    const ctx = gsap.context(() => {
      gsap.fromTo(
        mediaEl,
        { y: -60, scale: 1.12 },
        {
          y: 60,
          scale: 1.12,
          ease: 'none',
          scrollTrigger: {
            trigger: heroEl,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    }, heroEl);

    return () => ctx.revert();
  }, [heroImage]);

  if (project.externalLink) {
    metaRows.push({
      label: 'Visit',
      value: (
        <a
          href={project.externalLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          View Site
        </a>
      ),
    });
  }

  return (
    <div className="project-page">
      <section className="project-hero" ref={heroSectionRef}>
        {heroImage && (
          <div
            className="project-hero__media"
            ref={heroMediaRef}
            style={{ backgroundImage: `url(${heroImage})` }}
          >
            <div className="project-hero__media-overlay" aria-hidden="true" />
          </div>
        )}
      </section>

      <section className="project-content">
        <div className="wrapper">
          <div className="project-intro">
            <h1 className="project-title">{project.title}</h1>
            <p className="project-subtitle">{project.subtitle}</p>
          </div>

          <div className="project-hero__details">
            {project.intro && (
              <div className="project-hero__overview">
                <div className="project-overview">
                  <div className="project-overview__heading mono">Project overview</div>

                  <div className="project-overview__content">
                    {project.intro.split('\n\n').map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="project-meta-table">
              {metaRows.map((item) => (
                <div className="project-meta-row" key={item.label}>
                  <div className="project-meta-label mono">{item.label}</div>
                  <div className="project-meta-value mono">{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="project-gallery">
        <div className="wrapper">
          {project.heroBackgroundImage && (
            <div className="gallery-item">
              <img src={project.heroBackgroundImage} alt={`${project.title} hero`} />
            </div>
          )}

          {(project.blocks || []).map((block, index) => {
            if (block.type === 'image') {
              return (
                <div className="gallery-item" key={`${project.slug}-img-${index}`}>
                  <img src={block.src} alt={block.alt} />
                </div>
              );
            }

            if (block.type === 'two-images') {
              return (
                <div
                  className="gallery-item gallery-item--two-images"
                  key={`${project.slug}-two-img-${index}`}
                >
                  <div className="gallery-item__half">
                    <img src={block.images[0].src} alt={block.images[0].alt} />
                  </div>
                  <div className="gallery-item__half">
                    <img src={block.images[1].src} alt={block.images[1].alt} />
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>
      </section>
    </div>
  );
};

export default Project;
